"use client";
import { Globe2, ExternalLink } from "lucide-react";
import PageContent from "@/components/page-content";
import SectionHeader from "@/components/landing/section-header";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";


type Status = "best" | "neutral";
type ModelStat = { value: string; status: Status };
type ComparisonRow = {
  metric: string;
  moon: ModelStat;
  sam3: ModelStat;
  flash25: ModelStat;
  samgem: ModelStat;
};

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const MODEL_COL_WIDTH = "w-20 md:w-32 lg:w-40";

const Compare = () => {

  const comparisonData: ComparisonRow[] = [
    {
      metric: "RefCOCO-M",
      moon: { value: "88.2%", status: "best" },
      sam3: { value: "42.3%", status: "neutral" },
      flash25: { value: "73.9%", status: "neutral" },
      samgem: { value: "86.3%", status: "neutral" },
    },
    {
      metric: "RefCOCO",
      moon: { value: "83.2%", status: "best" },
      sam3: { value: "39.8%", status: "neutral" },
      flash25: { value: "66.6%", status: "neutral" },
      samgem: { value: "74.9%", status: "neutral" },
    },
    {
      metric: "RefCOCO+",
      moon: { value: "79.1%", status: "best" },
      sam3: { value: "27.0%", status: "neutral" },
      flash25: { value: "60.9%", status: "neutral" },
      samgem: { value: "66.9%", status: "neutral" },
    },
    {
      metric: "RefCOCOg",
      moon: { value: "80.7%", status: "best" },
      sam3: { value: "34.8%", status: "neutral" },
      flash25: { value: "68.1%", status: "neutral" },
      samgem: { value: "73.3%", status: "neutral" },
    },
    {
      metric: "LVIS",
      moon: { value: "62.6%", status: "neutral" },
      sam3: { value: "62.6%", status: "best" },
      flash25: { value: "--", status: "neutral" },
      samgem: { value: "--", status: "neutral" },
    },
    {
      metric: "Avg Time",
      moon: { value: "3.23s", status: "neutral" },
      sam3: { value: "0.4s", status: "best" },
      flash25: { value: "2.6s", status: "neutral" },
      samgem: { value: "10.5s", status: "neutral" },
    },
    {
      metric: "Cost / 1K images",
      moon: { value: "$0.40", status: "best" },
      sam3: { value: "$2.20", status: "neutral" },
      flash25: { value: "$9.00", status: "neutral" },
      samgem: { value: "$40.00", status: "neutral" },
    },
  ]

  function ModelTableCell({
    row,
    model,
  }: {
    row: ComparisonRow;
    model: keyof Omit<ComparisonRow, "metric">;
  }) {
    return (
      <TableCell
        className={cn(
          "py-4 text-muted-foreground text-center font-medium",
          MODEL_COL_WIDTH,
          model === "moon" && "bg-purple-200/30 dark:bg-purple-950/20",
          row[model].status === "best" && model === "moon" && "font-bold text-foreground",
          row[model].status === "best" && model !== "moon" && "text-foreground",
        )}
      >
        <div className="flex items-center justify-center gap-2">
          <span>{row[model].value}</span>
        </div>
      </TableCell>
    )
  }

  return (
    <PageContent>
      <SectionHeader
        sectionIcon={<Globe2 className="size-4" />}
        section="Benchmarks"
        heading="Top performance, single model"
        description="Moondream pairs top-tier grounding with precise segmentation in a single model, removing the need for any multi-model setup."
      />

      <div>
        <div className="relative overflow-hidden p-4 md:p-6">
          <div className="border-border/50 bg-background/50 relative overflow-x-auto border backdrop-blur-sm">
            <Table className="w-full min-w-[500px]">
              <TableHeader>
                <TableRow className="border-border/50 bg-muted/50">
                  <TableHead className="text-foreground font-semibold">
                  </TableHead>
                  <TableHead
                    className={cn(
                      "text-foreground text-center font-semibold bg-purple-200/50 dark:bg-purple-950/40 px-3 py-3",
                      MODEL_COL_WIDTH,
                    )}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Moondream
                    </div>
                  </TableHead>
                  <TableHead
                    className={cn(
                      "text-foreground text-center font-semibold px-3 py-3",
                      MODEL_COL_WIDTH,
                    )}
                  >
                    <div className="flex items-center justify-center gap-2">
                      SAM3
                    </div>
                  </TableHead>
                  <TableHead
                    className={cn(
                      "text-foreground text-center font-semibold px-3 py-3",
                      MODEL_COL_WIDTH,
                    )}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Gemini Flash
                    </div>
                  </TableHead>
                  <TableHead
                    className={cn(
                      "text-foreground text-center font-semibold px-3 py-3",
                      MODEL_COL_WIDTH,
                    )}
                  >
                    <div className="flex items-center justify-center gap-2">
                      SAM3 + Gemini 2.5 Pro
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.map((row, index) => (
                  <TableRow
                    key={index}
                    className={cn(
                      "border-border/30 hover:bg-muted/30 transition-colors",
                      index % 2 === 1 && "bg-muted/20",
                      row.metric === "Avg Time" && "border-t-2 border-t-border"
                    )}
                  >
                    <TableCell className="text-foreground py-4 font-medium">
                      {row.metric === "RefCOCO-M" ? (
                        <a
                          href="https://huggingface.co/datasets/moondream/refcoco-m"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5"
                        >
                          {row.metric}
                          <ExternalLink className="size-3 text-muted-foreground" />
                        </a>
                      ) : row.metric === "LVIS" ? (
                        "LVIS*"
                      ) : (
                        row.metric
                      )}
                    </TableCell>
                    <ModelTableCell row={row} model="moon" />
                    <ModelTableCell row={row} model="sam3" />
                    <ModelTableCell row={row} model="flash25" />
                    <ModelTableCell row={row} model="samgem" />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Methodology Section */}
          <Accordion type="single" collapsible className="mt-6">
            <AccordionItem value="methodology" className="border-border/50">
              <AccordionTrigger className="text-muted-foreground hover:text-foreground text-sm hover:no-underline">
                Methodology &amp; Notes
              </AccordionTrigger>
              <AccordionContent>
                <ul className="text-muted-foreground space-y-3 text-sm list-disc pl-5">
                  <li>
                    All reported scores are mIoU. We use the UNC data split for RefCOCO/RefCOCO+ and UMD for RefCOCOg.
                  </li>
                  <li>
                    SAM3 + Gemini 2.5 Pro is an agentic harness where the LLM calls SAM3 as a tool to find the best segmentation mask. This is SAM&apos;s recommended approach for optimal results.
                  </li>
                  <li>
                    SAM3 cost and speed measured on Roboflow.
                  </li>
                  <li>
                    Moondream cost is based on token usage and varies by image. The reported cost is averaged across many images.
                  </li>
                  <li>
                    LVIS*: This stat is from a previous release and will be updated soon. Moondream (62.61%) and SAM3 (62.64%) have nearly identical scores. We gave SAM3 the win since it was marginally higher. For LVIS, Moondream uses multi-instance mode where <code className="bg-muted px-1 rounded text-xs">point</code> identifies instances, then <code className="bg-muted px-1 rounded text-xs">segment</code> is called with the text prompt and point as spatial reference.
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </PageContent >
  );
};

export { Compare };
